import React, { CSSProperties, FC, memo, ReactNode, useCallback, useMemo, useState } from 'react';
import styles from './PIEAnnouncementBanner.module.scss';
import PIEDialog from '@src/shared/components/PIEDialog';
import { IconClose, IconInteractive, globalStyles } from 'snacks-design-system';
import PIEButton from '../PIEButton';
import PIEText from '../PIEText';
import { useMediaQuery } from '@material-ui/core';
import PIECheckbox from '../PIECheckbox';
import { useTranslation } from 'react-i18next';

export enum ANNOUNCEMENT_BANNER_BUTTON_POSITIONING {
  VERTICAL = 'vertical',
  HORIZONTAL = 'horizontal',
}

interface IPIEAnnouncementBanner {
  showBanner: Boolean;
  showCheckBox?: Boolean;
  title: string;
  content: string;
  checkBoxTitle?: string;
  showButtonTitle: string;
  showButtonIcon?: ReactNode;
  closeButtonIcon?: ReactNode;
  closeButtonTitle: string;
  bannerImage: string;
  onClose: (args: any) => void;
  onAction: () => void;
  onFormUpdate?: (e?: any) => void;
  headerBackgroundColor?: string;
  actionButtonsPositioning?: ANNOUNCEMENT_BANNER_BUTTON_POSITIONING;
  closeButtonCSS?: CSSProperties;
}

export const PIEAnnouncementBanner: FC<IPIEAnnouncementBanner> = ({
  showBanner,
  closeButtonTitle,
  closeButtonIcon,
  showButtonTitle,
  showButtonIcon,
  content,
  title,
  checkBoxTitle,
  bannerImage,
  showCheckBox = true,
  headerBackgroundColor,
  onClose,
  onAction,
  onFormUpdate,
  actionButtonsPositioning = ANNOUNCEMENT_BANNER_BUTTON_POSITIONING.HORIZONTAL,
  closeButtonCSS = {},
}) => {
  const isDesktopScreen = useMediaQuery(`(min-width: ${globalStyles.mqXSmallMax})`);
  const { t } = useTranslation();
  const [checkboxSelected, setCheckboxSelected] = useState<boolean>(false);

  /* Event Handlers */
  const handleOnClose = () => {
    onClose({ checkboxSelected });
  };

  const handleOnAction = () => {
    onAction();
  };

  const handleOnCheckboxChange = (e: any) => {
    if (e !== undefined && onFormUpdate) {
      setCheckboxSelected(e);
      onFormUpdate({ checkboxSelected: e });
    }
  };

  /* Section Components */
  const AnnounementBannerImage = useMemo(() => {
    return (
      (bannerImage && (
        <div
          className={styles.container}
          style={{ backgroundColor: `${headerBackgroundColor} !important` || '' }}
        >
          <img src={bannerImage} alt="" />
        </div>
      )) || <></>
    );
  }, [bannerImage]);

  const AnnouncementBannerClose = () => {
    return (
      <div className={styles.announcement_close} style={closeButtonCSS}>
        <IconInteractive onClick={handleOnClose}>
          <IconClose size="m" />
        </IconInteractive>
      </div>
    );
  };

  const AnnouncementBannerFooter = () => {
    return (
      <div
        className={`${styles.announcement_footer} ${
          actionButtonsPositioning === ANNOUNCEMENT_BANNER_BUTTON_POSITIONING.VERTICAL
            ? styles.announcement_footer_vertical_buttons
            : ''
        }`}
      >
        <PIEButton
          size="medium"
          type="button"
          mode="ghost"
          onClick={handleOnClose}
          text={`${t(closeButtonTitle)}`}
          autoWidth={true}
          icon={closeButtonIcon}
        />
        <PIEButton
          size="medium"
          type="button"
          mode="primary"
          autoWidth={true}
          wrapText={true}
          icon={showButtonIcon}
          onClick={handleOnAction}
          text={t(showButtonTitle)}
        />
      </div>
    );
  };

  const AnnouncementBannerContent = () => {
    return (
      <div className={styles.announcement_content}>
        <PIEText
          text={t(title)}
          size={`${isDesktopScreen ? 'var(--xds-font-size-24)' : 'var(--xds-font-size-20)'}`}
          fontWeight="var(--xds-font-weight-extrabold)"
          color="var(--xds-color-content-default)"
        />
        <PIEText
          text={t(content)}
          size="var(--xds-font-size-16)"
          fontWeight="var(--xds-font-weight-regular)"
          color="var(--xds-color-content-default)"
        />
        {showCheckBox && (
          <PIECheckbox onChange={handleOnCheckboxChange} checked={checkboxSelected}>
            <PIEText
              text={t(checkBoxTitle || '')}
              size="var(--xds-font-size-16)"
              fontWeight="var(--xds-font-weight-regular)"
              color="var(--xds-color-content-default)"
            />
          </PIECheckbox>
        )}
      </div>
    );
  };

  return (
    <div>
      <PIEDialog
        modCustom
        onClose={handleOnClose}
        modalBanner={AnnounementBannerImage}
        header={<AnnouncementBannerClose />}
        footer={<AnnouncementBannerFooter />}
        noContentPadding={true}
        isCenter
        isOpen={showBanner}
        size="small"
      >
        <AnnouncementBannerContent />
      </PIEDialog>
    </div>
  );
};

export default memo(PIEAnnouncementBanner, (prevProps, nextProps) => {
  return prevProps.showBanner === nextProps.showBanner;
});
